// ----------------------------------------------------------------------------
// Song Studio (admin). Rehearsal recordings in, chords and key out. Shares its
// parts and upload orchestration with the member page (/upload). Kept separate
// from the Song library, which holds YouTube-link songs.
// ----------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { CHORD_LIBRARY_SONG_LIMIT } from '@app/api/chords';
import { ErrorState, SkeletonRows, useToast } from '@app/components/ui';
import { useChordLibrary } from '../hooks/useChordLibrary';
import { UploadSongModal } from '../components/chords/UploadSongModal';
import { LibraryMeter, StudioDropZone, StudioRows, UploadBanner, countSaved, type UploadStatus } from '../components/chords/SongStudioParts';

type UploadArgs = [File, string, string | undefined, string | undefined, string | undefined];

export const ChordLibrary: React.FC = () => {
  const { user } = useAuth();
  const { church } = useChurch();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const churchId = user?.churchId ?? undefined;
  const { uploads, loading, loadError, uploadSong, removeUpload, renameUpload, reload } = useChordLibrary(churchId);
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [lastTitle, setLastTitle] = useState('');
  const lastArgs = useRef<UploadArgs | null>(null);

  const saved = countSaved(uploads);
  const atLimit = saved >= CHORD_LIBRARY_SONG_LIMIT;

  // Create → Upload to Song Studio arrives with ?upload=1.
  useEffect(() => {
    if (params.get('upload') === '1') {
      setUploadOpen(true);
      params.delete('upload');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // Same lifecycle as the member page: dismiss the result note after 12s.
  useEffect(() => {
    if (status !== 'done' && status !== 'error') return;
    const t = setTimeout(() => setStatus('idle'), 12000);
    return () => clearTimeout(t);
  }, [status]);

  const runUpload = async (...args: UploadArgs) => {
    lastArgs.current = args;
    setLastTitle(args[1]);
    setStatus('uploading');
    const result = await uploadSong(...args);
    setStatus(result === 'ok' ? 'done' : result === 'failed' ? 'error' : 'idle');
  };

  const openUpload = (file: File | null) => {
    setDroppedFile(file);
    setUploadOpen(true);
  };

  return (
    <div className="max-w-[900px]">
      <div className="flex flex-wrap items-end justify-between gap-[18px] mb-[22px]">
        <div className="flex-[1_1_360px] min-w-0">
          <div className="text-[13px] text-t4 mb-2">Open to everyone at {church?.name ?? 'your church'}</div>
          <h1 className="wp-h1 mb-2">Song Studio</h1>
          <p className="text-[14.5px] text-t3 leading-[1.6] wp-pretty">
            Upload a rehearsal recording from your computer and we&apos;ll detect the chords and key for your team. Any member can upload.
          </p>
          <p className="text-[12.5px] text-t4 mt-1.5 leading-normal">
            Kept separate from the <Link to="/admin/songs">Song library</Link>, which holds songs added from YouTube links.{' '}
            <Link to="/admin/tips">How Song Studio works</Link>
          </p>
        </div>
        <LibraryMeter count={saved} unit=" songs" className="flex-[0_1_260px] min-w-[220px]" />
      </div>

      <UploadBanner
        status={status}
        title={lastTitle}
        doneLine="Uploaded. You can leave this page."
        onDismiss={() => setStatus('idle')}
        onRetry={lastArgs.current ? () => runUpload(...lastArgs.current!) : undefined}
      />

      {loading ? (
        <SkeletonRows rows={4} />
      ) : loadError ? (
        <ErrorState title="Couldn't load Song Studio" body={loadError} onRetry={() => reload()} />
      ) : (
        <>
          <StudioDropZone
            empty={uploads.length === 0}
            atLimit={atLimit}
            ctaVariant="dark"
            onFile={openUpload}
            onReject={(m) => toast(m, 'error')}
          />
          <StudioRows uploads={uploads} onRemove={removeUpload} onRename={renameUpload} onRemoved={(t) => toast(`Removed "${t}". A slot is free in Song Studio.`)} />
        </>
      )}

      <UploadSongModal
        isOpen={isUploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setDroppedFile(null);
        }}
        onUpload={(...a) => runUpload(...(a as UploadArgs))}
        initialFile={droppedFile}
        existingUploads={uploads}
      />
    </div>
  );
};
